import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import * as APIModels from "../../../../models";
import canSubmit from "../forms/TylerDocumentUtils";
import {
    doAllDocumentsHaveFilingCode,
    hasNoSecurityTypesForFilingCode,
} from "../forms/TylerDocumentUtils";

// b12cff17-9515-4d78-85a5-b4990c369d6f
interface ValidationCheckModalProps {
    submission: APIModels.Submission;
    isOpen: boolean;
    handleClose: () => void;
    availableSecurityTypes?: any[];
    userRoleCode?: number;
}

interface ValidationResult {
    isValid: boolean;
    checks: {
        label: string;
        passed: boolean;
        message: string;
    }[];
}

export default function ValidationCheckModal(
    params: ValidationCheckModalProps
) {
    const {
        submission,
        isOpen,
        handleClose,
        availableSecurityTypes,
        userRoleCode,
    } = params;
    const [validationResult, setValidationResult] =
        useState<ValidationResult | null>(null);

    const runValidation = () => {
        // Check if user has permission (role code 600 or above)
        if (userRoleCode && userRoleCode < 600) {
            setValidationResult({
                isValid: false,
                checks: [
                    {
                        label: "Permission",
                        passed: false,
                        message:
                            "You do not have permission to run this validation check. Role code 600 or above required.",
                    },
                ],
            });
            return;
        }

        const docs = submission.documents || [];

        // Run all validation checks
        const checks = [];

        // Check if all documents have security types
        const hasAllSecurity = docs.every(
            (doc) =>
                doc.securityID !== null &&
                doc.securityID !== undefined &&
                doc.securityID !== "" &&
                doc.securityID !== APIModels.EmptyGuid
        );
        checks.push({
            label: "Security Types",
            passed: hasAllSecurity,
            message: hasAllSecurity
                ? "All documents have security types assigned"
                : "Some documents are missing security types",
        });

        // Check if all documents have filing codes
        const hasAllFilingCodes = doAllDocumentsHaveFilingCode(docs);
        checks.push({
            label: "Filing Codes",
            passed: hasAllFilingCodes,
            message: hasAllFilingCodes
                ? "All documents have filing codes assigned"
                : "Some documents are missing filing codes",
        });

        // Check if all documents have filing code components
        const hasAllComponents = docs.every(
            (doc) =>
                doc.filingComponentID !== null &&
                doc.filingComponentID !== undefined &&
                doc.filingComponentID !== "" &&
                doc.filingComponentID !== APIModels.EmptyGuid
        );
        checks.push({
            label: "Filing Components",
            passed: hasAllComponents,
            message: hasAllComponents
                ? "All documents have filing components assigned"
                : "Some documents are missing filing components",
        });

        // Run the main canSubmit function
        const canSubmitResult = canSubmit(
            submission,
            availableSecurityTypes || []
        );

        setValidationResult({
            isValid: canSubmitResult,
            checks: checks,
        });
    };

    useEffect(() => {
        if (isOpen) {
            runValidation();
        }
    }, [isOpen, submission, availableSecurityTypes]);

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Submission Validation Check</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {validationResult && (
                        <>
                            <Alert
                                severity={
                                    validationResult.isValid
                                        ? "success"
                                        : "error"
                                }
                            >
                                <AlertTitle>
                                    {validationResult.isValid
                                        ? "Validation Passed"
                                        : "Validation Failed"}
                                </AlertTitle>
                                {validationResult.isValid
                                    ? "This submission can be submitted."
                                    : "This submission cannot be submitted until all checks pass."}
                            </Alert>

                            <List>
                                {validationResult.checks.map((check, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            {check.passed ? (
                                                <CheckCircleIcon color="success" />
                                            ) : (
                                                <CancelIcon color="error" />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={check.label}
                                            secondary={check.message}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                <Button onClick={runValidation} variant="contained">
                    Re-check
                </Button>
            </DialogActions>
        </Dialog>
    );
}
